/* How old a credential check is, and what we are allowed to say about it.
 *
 * The rule this file exists to enforce: a verified badge never renders without
 * the date it was checked. "Verified" on its own is a claim about today made
 * with evidence from a day nobody named, and the person reading it is usually
 * deciding whether to hire the person it describes.
 *
 * Thresholds mirror accounts/credential_policy.py. The server sends `freshness`
 * on most payloads and these functions prefer it; the local computation is the
 * fallback for older payloads that only carry `last_verified_at`. If you change
 * a number here, change it there in the same commit.
 */

export const FRESH_DAYS = 90;
export const STALE_DAYS = 180;

export function checkAgeDays(lastVerifiedAt) {
  if (!lastVerifiedAt) return null;
  const ts = new Date(lastVerifiedAt);
  if (Number.isNaN(ts.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - ts.getTime()) / 86400000));
}

/** "fresh" | "aging" | "stale" | "unknown" */
export function freshnessOf(license) {
  if (!license) return "unknown";
  if (license.freshness) return license.freshness;       // server's answer wins
  const age = checkAgeDays(license.last_verified_at);
  if (age === null) return "unknown";
  if (age <= FRESH_DAYS) return "fresh";
  if (age <= STALE_DAYS) return "aging";
  return "stale";
}

/** Absolute date, not "3 months ago". A hiring decision wants the day. */
export function checkedOnLabel(lastVerifiedAt) {
  if (!lastVerifiedAt) return "never checked";
  const ts = new Date(lastVerifiedAt);
  if (Number.isNaN(ts.getTime())) return "never checked";
  return ts.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/* The phrase that goes next to the badge. Deliberately different words per
 * state rather than one sentence with a changing date: past a point we are no
 * longer reporting a live result, and the copy has to stop implying we are. */
export function checkedLabel(license) {
  const iso = license?.last_verified_at;
  switch (freshnessOf(license)) {
    case "fresh":
      return `Checked ${checkedOnLabel(iso)}`;
    case "aging":
      return `Last checked ${checkedOnLabel(iso)}`;
    case "stale":
      return `Not checked since ${checkedOnLabel(iso)}`;
    default:
      return "Not checked against a registry";
  }
}

/* Tooltip text. Says what the badge does and does not mean, in one place, so
 * every surface that shows a badge says the same thing. */
export function checkedTooltip(license, providerLabel = "") {
  const state = freshnessOf(license);
  const src = providerLabel ? `Source: ${providerLabel}.` : "";
  if (state === "unknown") {
    return [
      "Uploaded by the credential holder and not checked against any registry.",
      src,
    ].filter(Boolean).join(" ");
  }
  const when = `We asked the issuing registry on ${checkedOnLabel(license?.last_verified_at)}.`;
  const caveat =
    state === "stale"
      ? "That was long enough ago that it may no longer be current. Treat it as a past result, not today's."
      : "It reflects what the registry said on that date, not necessarily today.";
  return [when, caveat, src, "Sift does not score or rank people on credentials."]
    .filter(Boolean)
    .join(" ");
}

/** Tailwind classes for the pill, by freshness. Stale reads as neutral-grey
 *  rather than green: it is no longer a positive live signal. */
export function freshnessPillClasses(state, { dark = false } = {}) {
  if (dark) {
    switch (state) {
      case "fresh":  return "bg-emerald-900/50 text-emerald-300 border-emerald-700/60";
      case "aging":  return "bg-emerald-900/30 text-emerald-200/90 border-emerald-800/50";
      case "stale":  return "bg-gray-700/60 text-gray-300 border-gray-600";
      default:       return "bg-gray-700 text-gray-300 border-gray-600";
    }
  }
  switch (state) {
    case "fresh":  return "bg-emerald-50 text-emerald-800 border-emerald-200";
    case "aging":  return "bg-emerald-50/70 text-emerald-700 border-emerald-200/70";
    case "stale":  return "bg-gray-100 text-gray-600 border-gray-300";
    default:       return "bg-gray-100 text-gray-600 border-gray-300";
  }
}
